import React, { useId, useState } from 'react';

import { DoDont } from '@af/design-system-docs-ui/do-dont';
import { DoDontGrid } from '@af/design-system-docs-ui/do-dont-grid';
import Button from '@atlaskit/button/default/button';
import ChevronRightIcon from '@atlaskit/icon/core/chevron-right';
import ChevronUpIcon from '@atlaskit/icon/core/chevron-up';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Stack } from '@atlaskit/primitives/compiled/stack';

import assigneeDoDark from './_assets/assignee-do-dark.png';
import assigneeDo from './_assets/assignee-do.png';
import assigneeDontDark from './_assets/assignee-dont-dark.png';
import assigneeDont from './_assets/assignee-dont.png';
import attributeDoDark from './_assets/attribute_do-dark.png';
import attributeDo from './_assets/attribute_do.png';
import attributeDontDark from './_assets/attribute_dont-dark.png';
import attributeDont from './_assets/attribute_dont.png';
import automateDo1Dark from './_assets/automate-do-1-dark.png';
import automateDo1 from './_assets/automate-do-1.png';
import automateDo2Dark from './_assets/automate-do-2-dark.png';
import automateDo2 from './_assets/automate-do-2.png';
import automateDont1Dark from './_assets/automate-dont-1-dark.png';
import automateDont1 from './_assets/automate-dont-1.png';
import automateDont2Dark from './_assets/automate-dont-2-dark.png';
import automateDont2 from './_assets/automate-dont-2.png';
import avatarDo1Dark from './_assets/avatar-do-1-dark.png';
import avatarDo1 from './_assets/avatar-do-1.png';
import avatarDo2Dark from './_assets/avatar-do-2-dark.png';
import avatarDo2 from './_assets/avatar-do-2.png';
import avatarDo3Dark from './_assets/avatar-do-3-dark.png';
import avatarDo3 from './_assets/avatar-do-3.png';
import avatarDo4Dark from './_assets/avatar-do-4-dark.png';
import avatarDo4 from './_assets/avatar-do-4.png';
import avatarDont1Dark from './_assets/avatar-dont-1-dark.png';
import avatarDont1 from './_assets/avatar-dont-1.png';
import avatarDont2Dark from './_assets/avatar-dont-2-dark.png';
import avatarDont2 from './_assets/avatar-dont-2.png';
import avatarDont3Dark from './_assets/avatar-dont-3-dark.png';
import avatarDont3 from './_assets/avatar-dont-3.png';
import avatarDont4Dark from './_assets/avatar-dont-4-dark.png';
import avatarDont4 from './_assets/avatar-dont-4.png';
import cardsDoDark from './_assets/cards-do-dark.png';
import cardsDo from './_assets/cards-do.png';
import cardsDontDark from './_assets/cards-dont-dark.png';
import cardsDont from './_assets/cards-dont.png';
import contextDo1Dark from './_assets/context-do-1-dark.png';
import contextDo1 from './_assets/context-do-1.png';
import contextDo2Dark from './_assets/context-do-2-dark.png';
import contextDo2 from './_assets/context-do-2.png';
import contextDo3Dark from './_assets/context-do-3-dark.png';
import contextDo3 from './_assets/context-do-3.png';
import contextDo4Dark from './_assets/context-do-4-dark.png';
import contextDo4 from './_assets/context-do-4.png';
import contextDont1Dark from './_assets/context-dont-1-dark.png';
import contextDont1 from './_assets/context-dont-1.png';
import contextDont2Dark from './_assets/context-dont-2-dark.png';
import contextDont2 from './_assets/context-dont-2.png';
import contextDont3Dark from './_assets/context-dont-3-dark.png';
import contextDont3 from './_assets/context-dont-3.png';
import contextDont4Dark from './_assets/context-dont-4-dark.png';
import contextDont4 from './_assets/context-dont-4.png';
import groupDoDark from './_assets/group-do-dark.png';
import groupDo from './_assets/group-do.png';
import groupDontDark from './_assets/group-dont-dark.png';
import groupDont from './_assets/group-dont.png';
import groupOrderDoDark from './_assets/group-order-do-dark.png';
import groupOrderDo from './_assets/group-order-do.png';
import groupOrderDontDark from './_assets/group-order-dont-dark.png';
import groupOrderDont from './_assets/group-order-dont.png';
import hoverDoDark from './_assets/hover-do-dark.png';
import hoverDo from './_assets/hover-do.png';
import hoverDontDark from './_assets/hover-dont-dark.png';
import hoverDont from './_assets/hover-dont.png';
import sessionDoDark from './_assets/session-do-dark.png';
import sessionDo from './_assets/session-do.png';
import sessionDontDark from './_assets/session-dont-dark.png';
import sessionDont from './_assets/session-dont.png';

type GuidanceName =
	| 'avatars'
	| 'cards'
	| 'hover'
	| 'attribution'
	| 'presence'
	| 'session'
	| 'automation'
	| 'governance';

const examples: Record<
	GuidanceName,
	Array<{
		doImage: string;
		doImageDark: string;
		dontImage: string;
		dontImageDark: string;
		doAlt: string;
		dontAlt: string;
		doText: string;
		dontText: string;
	}>
> = {
	avatars: [
		{
			doImage: avatarDo1,
			doAlt: 'A lightning-bolt agent avatar uses the hexagonal shape reserved for agents.',
			doImageDark: avatarDo1Dark,
			dontImage: avatarDont1,
			dontAlt:
				'A lightning-bolt agent avatar incorrectly uses a circular shape, making it resemble a human avatar.',
			dontImageDark: avatarDont1Dark,
			doText:
				'Use the hexagon avatar for all agents, including those accessing the platform via CLI and MCP.',
			dontText:
				'Use any other shape for agent avatars, including circles, which are reserved for humans.',
		},
		{
			doImage: avatarDo2,
			doAlt:
				'Five agent avatars use a white icon on blue and black icons on green, purple, yellow and orange backgrounds.',
			doImageDark: avatarDo2Dark,
			dontImage: avatarDont2,
			dontAlt:
				'Agent avatars use the discouraged combinations of a black icon on blue and white icons on green, purple, yellow and orange.',
			dontImageDark: avatarDont2Dark,
			doText:
				'For first-party and custom agents, use one of the five team colours. Use black icons with most colours, but blue only works with white icons.',
			dontText:
				'Use black icons on blue avatars (it looks very muddy) or white icons on any other avatar colour (it is too low contrast).',
		},
		{
			doImage: avatarDo3,
			doAlt:
				'A hexagonal agent avatar sits beside a circular human avatar, clearly distinguishing the two.',
			doImageDark: avatarDo3Dark,
			dontImage: avatarDont3,
			dontAlt:
				'A human photo is incorrectly cropped into a hexagon beside an agent avatar, blurring the distinction between people and agents.',
			dontImageDark: avatarDont3Dark,
			doText: 'Use the hexagon avatar for agents.',
			dontText: 'Use the hexagon avatar for anything else.',
		},
		{
			doImage: avatarDo4,
			doAlt:
				'A hexagonal agent avatar extends above and below its square container to preserve its intended proportions.',
			doImageDark: avatarDo4Dark,
			dontImage: avatarDont4,
			dontAlt:
				'A hexagonal agent avatar is compressed vertically to fit inside a square, distorting its intended proportions.',
			dontImageDark: avatarDont4Dark,
			doText: 'Adhere to the defined proportions for agent avatars.',
			dontText:
				'Make the glyph neatly fit a square container. Proportions look bloated and unpleasant.',
		},
	],
	cards: [
		{
			doImage: cardsDo,
			doAlt:
				"Bug Report Assistant's lanyard-style card has an oval slot above its hexagonal avatar, followed by its name, owner and description.",
			doImageDark: cardsDoDark,
			dontImage: cardsDont,
			dontAlt:
				"Bug Report Assistant's lanyard-style card has an oval slot above its hexagonal avatar, followed by its name, owner and description.",
			dontImageDark: cardsDontDark,
			doText: 'Keep the cutout transparent to maintain the skeuomorphic “lanyard badge” effect.',
			dontText:
				'Set a solid fill in the lanyard cutout, thereby changing the cutout to a simple oval.',
		},
	],
	hover: [
		{
			doImage: hoverDo,
			doAlt:
				'A simplified agent hover card provides identifying information beside a picker without adding action buttons.',
			doImageDark: hoverDoDark,
			dontImage: hoverDont,
			dontAlt:
				'An agent hover card adds action buttons inside a picker, distracting from selecting an agent.',
			dontImageDark: hoverDontDark,
			doText: 'Use the simplified hover cards when hovering on agents in long lists and pickers.',
			dontText:
				'Use the full agent card with actions in long lists. Only use this card when hovering on individual agents.',
		},
	],
	attribution: [
		{
			doImage: attributeDo,
			doAlt:
				'Agent action attribution leads with a large hexagonal Rovo avatar and a smaller human avatar overlapping its lower-right corner.',
			doImageDark: attributeDoDark,
			dontImage: attributeDont,
			dontAlt:
				'Agent action attribution incorrectly leads with a large human avatar and relegates Rovo to a smaller overlapping avatar.',
			dontImageDark: attributeDontDark,
			doText: 'Always lead with the agent.',
			dontText: 'Lead with the invoking human.',
		},
	],
	presence: [
		{
			doImage: groupOrderDo,
			doAlt:
				"The collaborator stack places the current user's circular avatar first, followed by agent avatars and an additional-collaborator count.",
			doImageDark: groupOrderDoDark,
			dontImage: groupOrderDont,
			dontAlt: "The collaborator stack incorrectly places Rovo before the current user's avatar.",
			dontImageDark: groupOrderDontDark,
			doText:
				'In the presence pile, the current user’s avatar is always shown first. Then show agents followed by their associated humans in order of recency.',
			dontText:
				'Lead the presence pile with agents, or show humans and agents by anything other than order of recency.',
		},
		{
			doImage: groupDo,
			doAlt:
				'The collaborator stack includes both Rovo and Claude alongside the current user, keeping participating agents visible.',
			doImageDark: groupDoDark,
			dontImage: groupDont,
			dontAlt:
				'The collaborator stack omits Claude, showing only the current user, Rovo and an additional-collaborator count.',
			dontImageDark: groupDontDark,
			doText:
				'Show any active agents or agents that have taken action previously in an avatar stack.',
			dontText: 'Hide any agents. This includes MCP and CLI agents.',
		},
	],
	session: [
		{
			doImage: sessionDo,
			doAlt:
				"A Jira work item shows Claude's current activity, Checking available services, beneath the card.",
			doImageDark: sessionDoDark,
			dontImage: sessionDont,
			dontAlt:
				"A Jira work item shows only Claude's name beneath the card, without describing its progress or current activity.",
			dontImageDark: sessionDontDark,
			doText: 'Clearly communicate the agent’s progress and current state.',
			dontText: 'Obfuscate what the agent is doing by providing very limited information.',
		},
		{
			doImage: assigneeDo,
			doAlt:
				"Cursor's hexagonal avatar identifies it as the Jira work item's assignee, with its progress shown beneath the card.",
			doImageDark: assigneeDoDark,
			dontImage: assigneeDont,
			dontAlt:
				"A human remains shown as the assignee of a Jira work item delegated to Cursor, obscuring the agent's responsibility.",
			dontImageDark: assigneeDontDark,
			doText:
				'When an agent is also assigned to a work item, show the agent as the assignee on the board to provide a single owner for the work item.',
			dontText:
				'Show the user as assignee at board level. Whilst we provide more information about the invoking user on the work item details, lead with the assigned agent.',
		},
	],
	automation: [
		{
			doImage: automateDo1,
			doAlt:
				'Let this agent take action toggle, with an explanation that enabling it allows the agent to create, edit or delete using its own account.',
			doImageDark: automateDo1Dark,
			dontImage: automateDont1,
			dontAlt: 'Page version history records an edit by Rovo with Aoife Bourke.',
			dontImageDark: automateDont1Dark,
			doText: 'Provide the user with control over whether the agent can take action at all.',
			dontText:
				'Allow humans to be surprised when agents take unexpected actions due to automations.',
		},
		{
			doImage: automateDo2,
			doAlt:
				'Agent settings list available tools by app, including Jira actions to create, update and view work items, and identify that they act as the agent.',
			doImageDark: automateDo2Dark,
			dontImage: automateDont2,
			dontAlt:
				'Claude is checking available services for a Jira work item while the human remains the assignee.',
			dontImageDark: automateDont2Dark,
			doText: 'Provide more granular control over what tools the agent can use when taking action.',
			dontText:
				'Allow the agent to use any tools available with no limitations when taking action in automation.',
		},
	],
	governance: [
		{
			doImage: contextDo1,
			doAlt:
				"Page history identifies Canva with Roshini Malik and Figma with Aoife Bourke, alongside Liam's direct contribution.",
			doImageDark: contextDo1Dark,
			dontImage: contextDont1,
			dontAlt:
				'Page history lists only people, hiding the agents that contributed on their behalf.',
			dontImageDark: contextDont1Dark,
			doText: 'Provide visibility over all agent actions on the platform.',
			dontText: 'Hide any agent actions, including those by MCP and CLI agents.',
		},
		{
			doImage: contextDo4,
			doAlt:
				"Claude's attribution hover card identifies Anthropic, the associated person and Atlassian MCP as the connection method.",
			doImageDark: contextDo4Dark,
			dontImage: contextDont4,
			dontAlt:
				'Page history identifies a contributor only as Atlassian MCP, obscuring which agent used the connection.',
			dontImageDark: contextDont4Dark,
			doText:
				'Lead with the agent that took the action followed by the invoking human. Save technology — MCP, CLI, etc. — for the hover cards.',
			dontText:
				'Obscure which agent took an action and on whose behalf by only surfacing the technology used to access the platform.',
		},
		{
			doImage: contextDo2,
			doAlt:
				'Space access settings provide separate Add people and Add agents controls for managing access within a space.',
			doImageDark: contextDo2Dark,
			dontImage: contextDont2,
			dontAlt:
				"Bug fixer's profile offers global Grant access and Add to group controls without showing access controls for individual spaces or content.",
			dontImageDark: contextDont2Dark,
			doText: 'Provide granular control over agent access: units, apps, spaces and content.',
			dontText:
				'Only provide global access control to entire apps with no way to limit certain content or actions.',
		},
		{
			doImage: contextDo3,
			doAlt:
				"Prominent Suspend access and Deactivate account buttons let administrators stop an agent's access quickly.",
			doImageDark: contextDo3Dark,
			dontImage: contextDont3,
			dontAlt:
				"An agent's access is managed through separate app rows, without a visible control to suspend or deactivate the agent as a whole.",
			dontImageDark: contextDont3Dark,
			doText: 'Provide a clear deactivation control to quickly turn off a misbehaving agent.',
			dontText:
				'Force administrators to use granular controls to remove agent access piece by piece.',
		},
	],
};

const guidanceLabels: Record<GuidanceName, string> = {
	avatars: 'avatar guidance',
	cards: 'card guidance',
	hover: 'hover card guidance',
	attribution: 'attribution guidance',
	presence: 'avatar group guidance',
	session: 'session status guidance',
	automation: 'automation guidance',
	governance: 'governance guidance',
};

export const Guidance = ({ name }: { name: GuidanceName }): React.JSX.Element => {
	const [isExpanded, setIsExpanded] = useState(false);
	const panelId = useId();
	return (
		<Stack space="space.200">
			<div id={panelId} hidden={!isExpanded}>
				<Stack space="space.200">
					{examples[name].map(
						({
							doImage,
							doImageDark,
							dontImage,
							dontImageDark,
							doAlt,
							dontAlt,
							doText,
							dontText,
						}) => (
							<DoDontGrid key={doText} hasNoPaddingTop hasNoPaddingBottom>
								<DoDont type="do" image={{ url: doImage, urlDarkMode: doImageDark, alt: doAlt }}>
									{doText}
								</DoDont>
								<DoDont
									type="dont"
									image={{ url: dontImage, urlDarkMode: dontImageDark, alt: dontAlt }}
								>
									{dontText}
								</DoDont>
							</DoDontGrid>
						),
					)}
				</Stack>
			</div>
			<Box>
				<Button
					appearance="subtle"
					aria-controls={panelId}
					aria-expanded={isExpanded}
					iconAfter={() =>
						isExpanded ? (
							<ChevronUpIcon label="" size="small" />
						) : (
							<ChevronRightIcon label="" size="small" />
						)
					}
					onClick={() => setIsExpanded((expanded) => !expanded)}
				>
					{isExpanded ? 'Hide' : 'Show'} {guidanceLabels[name]}
				</Button>
			</Box>
		</Stack>
	);
};
